import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalGasStation as GasStationIcon,
} from '@mui/icons-material';
import { fuelPriceService } from '../services/api';
import { FuelPrice } from '../types';
import { divertilandiaColors } from '../theme/divertilandiaColors';

const FuelPrices: React.FC = () => {
  const [fuelPrices, setFuelPrices] = useState<FuelPrice[]>([]);
  const [currentPrices, setCurrentPrices] = useState<FuelPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPrice, setEditingPrice] = useState<FuelPrice | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    fuel_type: 'regular' as 'regular' | 'premium' | 'diesel' | 'electricity',
    price_per_liter: 0,
    effective_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const fuelTypes = [
    { value: 'regular', label: 'Gasolina Roja', color: '#e74c3c' },
    { value: 'premium', label: 'Gasolina Verde', color: '#27ae60' },
    { value: 'diesel', label: 'Diésel', color: '#34495e' },
    { value: 'electricity', label: 'Electricidad (kWh)', color: '#3498db' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [allPrices, current] = await Promise.all([
        fuelPriceService.getAll(),
        fuelPriceService.getCurrentPrices()
      ]);
      
      setFuelPrices(Array.isArray(allPrices) ? allPrices : []);
      setCurrentPrices(Array.isArray(current) ? current : []);
    } catch (err) {
      setError('Error al cargar los precios de combustible');
      console.error('Error fetching fuel prices:', err);
      setFuelPrices([]);
      setCurrentPrices([]);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    const errorMessages: string[] = [];

    // Validar tipo de combustible
    if (!formData.fuel_type) {
      errors.fuel_type = 'El tipo de combustible es obligatorio';
      errorMessages.push('• El tipo de combustible es obligatorio');
    }

    // Validar precio
    if (formData.price_per_liter <= 0) {
      errors.price_per_liter = 'El precio debe ser mayor a 0';
      errorMessages.push('• El precio debe ser mayor a 0');
    }

    // Validar fecha
    if (!formData.effective_date) {
      errors.effective_date = 'La fecha efectiva es obligatoria';
      errorMessages.push('• La fecha efectiva es obligatoria');
    }

    return { errors, errorMessages };
  };

  const handleSave = async () => {
    try {
      setError(null);
      setValidationErrors({});

      // Validar el formulario
      const { errors, errorMessages } = validateForm();
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setErrorMessages(errorMessages);
        setOpenErrorDialog(true);
        return;
      }

      if (editingPrice) {
        await fuelPriceService.update(editingPrice.id, formData);
      } else {
        await fuelPriceService.create(formData);
      }
      fetchData();
      handleCloseDialog();
    } catch (err: any) {
      console.error('Error saving fuel price:', err);
      
      // Manejar errores del backend
      if (err.response?.data?.errors) {
        const backendErrors = err.response.data.errors;
        const errorMessages: string[] = [];
        const validationErrors: {[key: string]: string} = {};

        Object.keys(backendErrors).forEach(key => {
          const messages = backendErrors[key];
          if (Array.isArray(messages)) {
            errorMessages.push(...messages.map(msg => `• ${msg}`));
            validationErrors[key] = messages[0];
          }
        });

        setValidationErrors(validationErrors);
        setErrorMessages(errorMessages);
        setOpenErrorDialog(true);
      } else {
        setError('Error al guardar el precio de combustible');
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este precio de combustible?')) {
      try {
        await fuelPriceService.delete(id);
        fetchData();
      } catch (err) {
        setError('Error al eliminar el precio de combustible');
        console.error('Error deleting fuel price:', err);
      }
    }
  };

  const handleOpenDialog = (price?: FuelPrice) => {
    if (price) {
      setEditingPrice(price);
      setFormData({
        fuel_type: price.fuel_type,
        price_per_liter: price.price_per_liter,
        effective_date: price.effective_date,
        notes: price.notes || '',
      });
    } else {
      setEditingPrice(null);
      setFormData({
        fuel_type: 'regular',
        price_per_liter: 0,
        effective_date: new Date().toISOString().split('T')[0],
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPrice(null);
    setValidationErrors({});
    setErrorMessages([]);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    // Crear fecha local sin problemas de zona horaria
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('es-MX');
  };

  const getFuelTypeInfo = (type: string) => {
    return fuelTypes.find(ft => ft.value === type) || { label: type, color: '#95A5A6' };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: divertilandiaColors.standard.primary, fontWeight: 'bold' }}>
            Precios de Combustible
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestiona los precios actuales de gasolina roja y verde
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: divertilandiaColors.rainbow.green,
            '&:hover': {
              backgroundColor: divertilandiaColors.rainbow.green,
              opacity: 0.9,
            },
          }}
        >
          Nuevo Precio
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Precios Actuales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2, color: divertilandiaColors.standard.primary }}>
            Precios Actuales
          </Typography>
        </Grid>
        {(currentPrices || []).map((price) => {
          const fuelInfo = getFuelTypeInfo(price.fuel_type);
          return (
            <Grid item xs={12} md={6} key={price.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <GasStationIcon sx={{ mr: 1, color: fuelInfo.color }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {fuelInfo.label}
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ color: fuelInfo.color, fontWeight: 'bold', mb: 1 }}>
                    {formatPrice(price.price_per_liter)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Válido desde: {formatDate(price.effective_date)}
                  </Typography>
                  {price.notes && (
                    <Typography variant="body2" color="text.secondary">
                      {price.notes}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Historial de Precios */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2, color: divertilandiaColors.standard.primary }}>
            Historial de Precios
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tipo de Combustible</TableCell>
                  <TableCell>Precio por Litro</TableCell>
                  <TableCell>Fecha Efectiva</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Notas</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(fuelPrices || []).map((price) => {
                  const fuelInfo = getFuelTypeInfo(price.fuel_type);
                  return (
                    <TableRow key={price.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              backgroundColor: fuelInfo.color,
                              borderRadius: '50%',
                              mr: 1,
                            }}
                          />
                          {fuelInfo.label}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {formatPrice(price.price_per_liter)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(price.effective_date)}</TableCell>
                      <TableCell>
                        <Chip
                          label={price.is_active ? 'Activo' : 'Inactivo'}
                          color={price.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {price.notes ? (
                          <Typography variant="body2" color="text.secondary">
                            {price.notes}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Sin notas
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(price)}
                            sx={{ color: divertilandiaColors.rainbow.blue }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(price.id)}
                            sx={{ color: divertilandiaColors.rainbow.red }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Dialog para crear/editar precio */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPrice ? 'Editar Precio de Combustible' : 'Nuevo Precio de Combustible'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!validationErrors.fuel_type}>
                <InputLabel>Tipo de Combustible</InputLabel>
                <Select
                  value={formData.fuel_type}
                  onChange={(e) => handleInputChange('fuel_type', e.target.value)}
                  label="Tipo de Combustible"
                >
                  {fuelTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: type.color,
                            borderRadius: '50%',
                            mr: 1,
                          }}
                        />
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.fuel_type && (
                  <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                    {validationErrors.fuel_type}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={formData.fuel_type === 'electricity' ? 'Precio por kWh' : 'Precio por Litro'}
                type="number"
                value={formData.price_per_liter}
                onChange={(e) => handleInputChange('price_per_liter', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.01 }}
                required
                error={!!validationErrors.price_per_liter}
                helperText={validationErrors.price_per_liter || (formData.fuel_type === 'electricity' ? 'Precio por kilovatio-hora (kWh)' : 'Precio por litro de combustible')}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha Efectiva"
                type="date"
                value={formData.effective_date}
                onChange={(e) => handleInputChange('effective_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                error={!!validationErrors.effective_date}
                helperText={validationErrors.effective_date}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas (Opcional)"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Ej: Aumento por inflación, cambio de proveedor, etc."
              />
            </Grid>

            {formData.price_per_liter > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="h6" gutterBottom>
                    Resumen del Precio
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    <strong>Tipo:</strong> {getFuelTypeInfo(formData.fuel_type).label}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    <strong>Precio:</strong> {formatPrice(formData.price_per_liter)} por litro
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    <strong>Fecha efectiva:</strong> {formatDate(formData.effective_date)}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            {editingPrice ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Errores de Validación */}
      <Dialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
          <Alert severity="error" sx={{ mr: 2, p: 0, backgroundColor: 'transparent' }}>
            ⚠️
          </Alert>
          Error de Validación
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            No se pudo guardar el precio de combustible. Por favor corrige los siguientes errores:
          </Typography>
          <Box sx={{ mt: 2 }}>
            {errorMessages.map((message, index) => (
              <Typography key={index} variant="body1" sx={{ mb: 1, color: 'error.main' }}>
                {message}
              </Typography>
            ))}
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Los campos marcados en rojo son obligatorios o tienen valores inválidos.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenErrorDialog(false)} variant="contained">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FuelPrices;
